import { TDOId, TreeObjectId } from '@cbsa/state/modules/universal';
import { BOOT } from '@common-modules/app';
import { DEFAULT_PER_PAGE } from '@helpers/constants';
import { CaseReducer, createReducer } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';
import { omit } from 'lodash';
import { sortFolderPath } from '../../helper';
import {
  CLEAR_TRASH_SELECTION,
  FETCH_CASE_FILES_FAILURE,
  FETCH_CASE_FILES_REQUEST,
  FETCH_CASE_FILES_SUCCESS,
  FETCH_TRASH_FOLDERS,
  FETCH_TRASH_FOLDERS_FAILURE,
  FETCH_TRASH_TDOS,
  FETCH_TRASH_TDOS_FAILURE,
  SET_FILES_TABLE_PAGE_INDEX,
  SET_FILES_TABLE_PAGE_SIZE,
  SET_FOLDERS_TABLE_PAGE_INDEX,
  SET_FOLDERS_TABLE_PAGE_SIZE,
  SET_TRASH_FOLDERS_DATA,
  SET_TRASH_TDOS_DATA,
  UPDATE_SELECTED_FILE_ITEMS,
  UPDATE_SELECTED_FOLDER_ITEMS,
  UPDATE_TRASH_FILE_DATA_STORE,
  UPDATE_TRASH_FOLDER_DATA_STORE,
} from '../actions';
import {
  CaseFilesState,
  pickTrashContentTemplate,
  TrashState,
} from '../models';
import type { SetTrashFoldersPayload, SetTrashTDOsPayload } from '../services';
import { defaultState, namespace } from '../store';

type Re<P = unknown> = CaseReducer<TrashState, { payload: P; type: string }>;

const emptyCaseFiles = (): CaseFilesState => ({
  records: [],
  hasNextPage: false,
  pageIndex: 0,
  pageSize: DEFAULT_PER_PAGE,
  isLoading: false,
  loadError: false,
  hasLoadedOnce: false,
});

// Snapshot the rows behind a selection. The current page wins (freshest data);
// otherwise keep the snapshot taken when the row was ticked on an earlier page.
// Ids resolvable from neither are dropped here and reported by the saga.
const snapshotSelection = <T>(
  ids: readonly string[],
  currentPage: { readonly [key: string]: T },
  previous: { readonly [key: string]: T }
): { [key: string]: T } =>
  Object.fromEntries(
    ids.flatMap((id) => {
      const row = currentPage[id] ?? previous[id];
      return row ? [[id, row] as const] : [];
    })
  );

const fetchTrashFileData: Re = (state) => ({
  ...state,
  files: {
    ...state.files,
    tdos: {},
    isFetchingData: true,
  },
});

const fetchTrashFileDataFailure: Re = (state) => ({
  ...state,
  files: {
    ...state.files,
    isFetchingData: false,
  },
});

const fetchTrashTDOsSuccess: Re<SetTrashTDOsPayload> = (
  state,
  { payload }
) => ({
  ...state,
  files: {
    ...state.files,
    isFetchingData: false,
    tdos: Object.fromEntries(
      Object.entries(payload.folder.childTDOs.records).map(([_, value]) => [
        value.id,
        {
          ...value,
          // `folders` can be absent on a partial (not_found) response — default
          // to [] so one dangling row can't crash the whole files table.
          folders: (value.folders ?? []).map((f) => ({
            folderPath: sortFolderPath(f.folderPath),
          })),
        },
      ])
    ),
    hasNextPage: payload.folder.hasNextPage,
  },
});

const updateSelectedFileItems: Re<{ tdoIds: TDOId[] }> = (
  state,
  { payload }
) => ({
  ...state,
  files: {
    ...state.files,
    selectedItems: payload.tdoIds,
    selectedTdos: snapshotSelection(
      payload.tdoIds,
      state.files.tdos,
      state.files.selectedTdos
    ),
  },
});

const clearTrashSelection = (state: TrashState): TrashState => ({
  ...state,
  files: { ...state.files, selectedItems: [], selectedTdos: {} },
  folders: { ...state.folders, selectedItems: [], selectedFolders: {} },
});

const updateTrashFileDataStore: Re<{ tdoIds: TDOId[] }> = (
  state,
  { payload }
) => {
  const tdos = omit(state.files.tdos, payload.tdoIds);
  // The next-page flag is refreshed by the refetch that follows a restore /
  // permanent delete, so nothing to adjust here beyond dropping the rows.
  return {
    ...state,
    files: {
      ...state.files,
      tdos,
      isFetchingData: false,
    },
  };
};

const fetchTrashFolderData: Re = (state) => ({
  ...state,
  folders: {
    ...state.folders,
    folders: {},
    caseFiles: {},
    isFetchingData: true,
  },
});

const fetchTrashFoldersSuccess: Re<SetTrashFoldersPayload> = (
  state,
  { payload }
) => ({
  ...state,
  folders: {
    ...state.folders,
    isFetchingData: false,
    folders: Object.fromEntries(
      Object.entries(payload.folder.childFolders.records)
        .filter(([_, value]) => !!value.treeObjectId)
        .map(([_, value]) => [
          value.treeObjectId,
          {
            ...value,
            folderPath: sortFolderPath(value.folderPath),
            // Resolve the trash SDO once here so every consumer reads trash
            // metadata from the right template instead of contentTemplates[0].
            trashContentTemplate: pickTrashContentTemplate(
              value.contentTemplates
            ),
          },
        ])
    ),
    hasNextPage: payload.folder.hasNextPage,
  },
});

const fetchTrashFolderDataFailure: Re = (state) => ({
  ...state,
  folders: {
    ...state.folders,
    isFetchingData: false,
  },
});

const updateSelectedFolderItems: Re<{ treeObjectId: TreeObjectId[] }> = (
  state,
  { payload }
) => ({
  ...state,
  folders: {
    ...state.folders,
    selectedItems: payload.treeObjectId,
    selectedFolders: snapshotSelection(
      payload.treeObjectId,
      state.folders.folders,
      state.folders.selectedFolders
    ),
  },
});

const updateTrashFolderDataStore: Re<{ treeObjectIds: TreeObjectId[] }> = (
  state,
  { payload }
) => {
  const folders = omit(state.folders.folders, payload.treeObjectIds);
  // The next-page flag is refreshed by the refetch that follows a restore /
  // permanent delete, so nothing to adjust here beyond dropping the rows.
  return {
    ...state,
    folders: {
      ...state.folders,
      folders,
      caseFiles: omit(state.folders.caseFiles, payload.treeObjectIds),
      isFetchingData: false,
    },
  };
};

const fetchCaseFilesRequest: Re<{ treeObjectId: TreeObjectId }> = (
  state,
  { payload }
) => {
  const prev =
    state.folders.caseFiles[payload.treeObjectId] ?? emptyCaseFiles();
  return {
    ...state,
    folders: {
      ...state.folders,
      caseFiles: {
        ...state.folders.caseFiles,
        [payload.treeObjectId]: {
          ...prev,
          isLoading: true,
          loadError: false,
        },
      },
    },
  };
};

const fetchCaseFilesSuccess: CaseReducer<
  TrashState,
  ReturnType<typeof FETCH_CASE_FILES_SUCCESS>
> = (state, action) => {
  const childTDOs = action.payload.folder.childTDOs;
  const { treeObjectId, limit, offset } = action.meta.variables;
  // N+1 lookahead: the service fetched `limit + 1` rows. If it returned more
  // than `limit`, a next page exists; keep only the first `limit` for display.
  const rawRecords = childTDOs.records;
  const hasNextPage = rawRecords.length > limit;
  const records = hasNextPage ? rawRecords.slice(0, limit) : rawRecords;
  return {
    ...state,
    folders: {
      ...state.folders,
      caseFiles: {
        ...state.folders.caseFiles,
        [treeObjectId]: {
          records,
          hasNextPage,
          pageIndex: limit > 0 ? Math.floor(offset / limit) : 0,
          pageSize: limit,
          isLoading: false,
          loadError: false,
          hasLoadedOnce: true,
        },
      },
    },
  };
};

const fetchCaseFilesFailure: Re<{ treeObjectId: TreeObjectId }> = (
  state,
  { payload }
) => {
  const prev =
    state.folders.caseFiles[payload.treeObjectId] ?? emptyCaseFiles();
  return {
    ...state,
    folders: {
      ...state.folders,
      caseFiles: {
        ...state.folders.caseFiles,
        [payload.treeObjectId]: {
          ...prev,
          isLoading: false,
          loadError: true,
        },
      },
    },
  };
};

const setFilesTablePageIndex: Re<{ pageIndex: number }> = (
  state,
  { payload }
) => {
  const { pageIndex } = payload;
  return {
    ...state,
    files: {
      ...state.files,
      pageIndex,
    },
  };
};

const setFilesTablePageSize: Re<{ pageSize: number }> = (
  state,
  { payload }
) => {
  const { pageSize } = payload;
  return {
    ...state,
    files: {
      ...state.files,
      pageSize,
      pageIndex: 0,
    },
  };
};

const setFoldersTablePageIndex: Re<{ pageIndex: number }> = (
  state,
  { payload }
) => {
  const { pageIndex } = payload;
  return {
    ...state,
    folders: {
      ...state.folders,
      pageIndex,
    },
  };
};

const setFoldersTablePageSize: Re<{ pageSize: number }> = (
  state,
  { payload }
) => {
  const { pageSize } = payload;
  return {
    ...state,
    folders: {
      ...state.folders,
      pageSize,
      pageIndex: 0,
    },
  };
};

export const trashReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(FETCH_TRASH_TDOS, fetchTrashFileData)
      .addCase(SET_TRASH_TDOS_DATA, fetchTrashTDOsSuccess)
      .addCase(FETCH_TRASH_TDOS_FAILURE, fetchTrashFileDataFailure)
      .addCase(UPDATE_SELECTED_FILE_ITEMS, updateSelectedFileItems)
      .addCase(CLEAR_TRASH_SELECTION, clearTrashSelection)
      // A new session must never inherit the previous one's selection snapshot.
      .addCase(modules.user.LOGOUT, clearTrashSelection)
      .addCase(BOOT, clearTrashSelection)
      .addCase(UPDATE_TRASH_FILE_DATA_STORE, updateTrashFileDataStore)
      .addCase(FETCH_TRASH_FOLDERS, fetchTrashFolderData)
      .addCase(SET_TRASH_FOLDERS_DATA, fetchTrashFoldersSuccess)
      .addCase(FETCH_TRASH_FOLDERS_FAILURE, fetchTrashFolderDataFailure)
      .addCase(FETCH_CASE_FILES_REQUEST, fetchCaseFilesRequest)
      .addCase(FETCH_CASE_FILES_SUCCESS, fetchCaseFilesSuccess)
      .addCase(FETCH_CASE_FILES_FAILURE, fetchCaseFilesFailure)
      .addCase(UPDATE_SELECTED_FOLDER_ITEMS, updateSelectedFolderItems)
      .addCase(UPDATE_TRASH_FOLDER_DATA_STORE, updateTrashFolderDataStore)
      .addCase(SET_FILES_TABLE_PAGE_INDEX, setFilesTablePageIndex)
      .addCase(SET_FILES_TABLE_PAGE_SIZE, setFilesTablePageSize)
      .addCase(SET_FOLDERS_TABLE_PAGE_INDEX, setFoldersTablePageIndex)
      .addCase(SET_FOLDERS_TABLE_PAGE_SIZE, setFoldersTablePageSize);
  }),
};
